import {MatLegacyFormFieldControl as MatFormFieldControl} from '@angular/material/legacy-form-field';
import {Directive, DoCheck, forwardRef, HostBinding, Input, OnDestroy, OnInit, Optional, Self} from '@angular/core';
import {Subject} from 'rxjs';
import {
    ControlValueAccessor,
    UntypedFormControl,
    FormGroupDirective,
    NgControl,
    NgForm
} from '@angular/forms';
import {coerceBooleanProperty} from '@angular/cdk/coercion';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {ErrorStateMatcher} from '@angular/material/core';
import {ChipListMultiselectComponent} from '@mydocma/component-library/src/lib/chips';
import {ChipListMultiselectItem} from '@mydocma/component-library/src/lib/chips';

export class ChipsMultiselectErrorStateMatcher {
    constructor(private chipsMultiselect: ChipsMultiselectFieldDirective) {
    }

    isErrorState(control: UntypedFormControl | null, form: FormGroupDirective | NgForm | null): boolean {
        if (!control) {
            return this.chipsMultiselect.required && this.chipsMultiselect.empty;
        } else {
            return !!(control && control.invalid && (control.touched || (form && form.submitted)));
        }
    }
}

@UntilDestroy()
@Directive({
    selector: '[mydocmaChipsMultiselectField]',
    providers: [{provide: MatFormFieldControl, useExisting: forwardRef(() => ChipsMultiselectFieldDirective)}],
})
export class ChipsMultiselectFieldDirective implements MatFormFieldControl<ChipListMultiselectItem[]>, ControlValueAccessor, OnDestroy, DoCheck, OnInit {

    static nextId = 0;

    controlType = 'mydocma-chip-list-multiselect';
    autofilled?: boolean;

    @HostBinding()
    @Input()
    id = `chips-multiselect-field-${ChipsMultiselectFieldDirective.nextId++}`;

    @HostBinding('attr.aria-describedby')
    describedBy = '';

    errorState = false;
    @Input() errorStateMatcher?: ErrorStateMatcher;
    
    stateChanges = new Subject<void>();
    focused = false;
    
    private _defaultErrorStateMatcher: ErrorStateMatcher = new ChipsMultiselectErrorStateMatcher(this);
    private onChange: (value: ChipListMultiselectItem[] | null) => void = () => {};
    private onTouched: () => void = () => {};

    constructor(
        private host: ChipListMultiselectComponent,
        @Optional() @Self() public ngControl: NgControl,
        @Optional() private _parentForm: NgForm,
        @Optional() private _parentFormGroup: FormGroupDirective,
    ) {
        if (this.ngControl) {
            this.ngControl.valueAccessor = this;
        }

        this.host.dropdownOpenChange
            .pipe(untilDestroyed(this))
            .subscribe((open) => {
                this.focused = open;
                if (!open) {
                    this.onTouched();
                }
                this.stateChanges.next();
            });

        this.host.selectionChange
            .pipe(untilDestroyed(this))
            .subscribe((selected) => {
                this._value = this.normalizeValue(selected);
                this.onChange(this._value);
                this.onTouched();
                this.stateChanges.next();
            });
    }


    get empty(): boolean {
        return this._value == null || (Array.isArray(this._value) && this._value.length === 0);
    }

    get shouldLabelFloat() {
        return this.focused || !this.empty;
    }

    get readonly(): boolean {
        return this.host.readonly;
    }

    private _placeholder = '';

    @Input()
    get placeholder(): string {
        return this._placeholder;
    }

    set placeholder(value: string) {
        this._placeholder = value;
        this.stateChanges.next();
    }

    private _required = false;

    @Input()
    get required(): boolean {
        return this._required;
    }

    set required(value: boolean) {
        this._required = coerceBooleanProperty(value);
        this.stateChanges.next();
    }

    private _disabled = false;

    @Input()
    get disabled(): boolean {
        return this._disabled || this.host.readonly;
    }

    set disabled(value: boolean) {
        this._disabled = coerceBooleanProperty(value);
        this.stateChanges.next();
    }

    private _value: ChipListMultiselectItem[] | null = null;

    @Input()
    get value(): ChipListMultiselectItem[] | null {
        return this._value;
    }

    set value(v: ChipListMultiselectItem[] | null) {
        this.writeValue(v);
        this.onChange(this._value);
        this.onTouched();
    }

    ngOnInit() {
        const ngControl = this.ngControl;
        if (ngControl) {
            this._value = this.normalizeValue(ngControl.value);
            this._disabled = !!ngControl.disabled;
            ngControl.statusChanges?.pipe(untilDestroyed(this)).subscribe(s => {
                const disabled = s === 'DISABLED';
                if (disabled !== this._disabled) {
                    this.disabled = disabled;
                }
            });
            ngControl.valueChanges?.pipe(untilDestroyed(this)).subscribe(v => {
                this._value = this.normalizeValue(v);
                this.applyValueToHost(this._value);
                this.stateChanges.next();
            });
        }
    }

    ngOnDestroy() {
        this.stateChanges.complete();
    }

    ngDoCheck() {
        // We need to re-evaluate this on every change detection cycle, because there are some
        // error triggers that we can't subscribe to (e.g. parent form submissions). This means
        // that whatever logic is in here has to be super lean or we risk destroying the performance.
        this.updateErrorState();
    }

    writeValue(value: ChipListMultiselectItem[] | null): void {
        this._value = this.normalizeValue(value);
        this.applyValueToHost(this._value);
        this.stateChanges.next();
    }

    registerOnChange(fn: (value: ChipListMultiselectItem[] | null) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    updateErrorState() {
        const oldState = this.errorState;
        const parent = this._parentFormGroup || this._parentForm;
        const matcher = this.errorStateMatcher || this._defaultErrorStateMatcher;
        const control = this.ngControl ? this.ngControl.control as UntypedFormControl : null;
        const newState = matcher.isErrorState(control, parent);

        if (newState !== oldState) {
            this.errorState = newState;
            this.stateChanges.next();
        }
    }

    setDescribedByIds(ids: string[]): void {
        this.describedBy = ids.join(' ');
    }

    onContainerClick(event: MouseEvent): void {
        if (this.host.readonly || !this.host.options?.openDropdownOnClick) {
            return;
        }

        const target = event.target as HTMLElement;
        if (target.closest('mat-chip') || target.closest('button')) {
            return;
        }

        this.host.openDropdown();
    }

    private applyValueToHost(value: ChipListMultiselectItem[] | null): void {
        if (!value?.length) {
            this.host.clearSelectedItems();
        }
    }

    private normalizeValue(value: ChipListMultiselectItem[] | null): ChipListMultiselectItem[] | null {
        if (value == null || (Array.isArray(value) && value.length === 0)) {
            return null;
        }

        return value;
    }
}
